import Image from 'next/legacy/image';
import styled, { css } from 'styled-components';
import type { ImageProps } from 'next/legacy/image';
import { Box, Heading, Link, Paragraph } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors, spaces } from '@99designs/emu/settings';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';

interface StepProps {
    title: string;
    body: string;
    step: number;
    image: ImageProps;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    activeStep: number;
}

const Wrapper = styled(Box)<{ step: number }>`
    display: flex;
    flex-direction: column;
    position: relative;
    align-self: flex-start;
    margin-left: ${spaces.px['6bu']}px;
    width: 250px;

    &:first-child {
        margin-left: 0;
    }

    ${mediaQuery.minimum('normal')} {
        margin-top: ${({ step }) => `${spaces.px['2bu'] * step - 1}px`};
    }

    ${mediaQuery.only('medium')} {
        margin-top: ${spaces.px['4bu']}px;

        &:last-child {
            margin-right: auto;
            margin-left: 56px;
        }
    }

    ${mediaQuery.maximum('small')} {
        margin: 0 0 ${spaces.px['6bu']}px 0;

        &:last-child {
            margin: 0;
        }
    }
`;

const HeadingWrapper = styled(Box)<{ isActive: boolean }>`
    background-color: ${colors.white};
    border: ${spaces.px['0.75bu']}px solid ${colors.grey100};
    border-color: ${({ isActive }) => isActive && colors.grey800};
    padding: ${spaces.px['1bu']}px;
    margin-bottom: ${spaces.px['4bu']}px;
    width: 250px;
    height: 250px;
    position: relative;
    display: flex;
    align-self: flex-end;
    z-index: 1;

    ${mediaQuery.maximum('medium')} {
        border-color: ${colors.grey800};
    }
`;

const StyledHeading = styled(Heading)`
    align-self: flex-end;
    z-index: 2;
`;

const StepNumber = styled(Heading)`
    font-size: 280px;
    position: absolute;
    right: 10%;
    top: 10%;
    z-index: 1;
`;

const customImageWrapperStyles = (step: number): ReturnType<typeof css> => {
    switch (step) {
        case 1:
            return css`
                transform: translate(-45%, -25%);
            `;
        case 2:
            return css`
                width: 400px;
                transform: translate(-18%, -20%);
            `;
        case 3:
            return css`
                transform: translate(72%, 57%);

                ${mediaQuery.maximum('medium')} {
                    transform: translate(72%, 17%);
                }
            `;
        default:
            return css``;
    }
};

const ImageWrapper = styled(Box)<{ step: number; isActive: boolean }>`
    position: absolute;
    z-index: 0;
    display: none;
    display: ${({ isActive }) => isActive && 'block'};

    ${({ step }) => customImageWrapperStyles(step)};

    ${mediaQuery.maximum('medium')} {
        display: block;
    }
`;

export function Step({
    title,
    body,
    step,
    image,
    onMouseEnter,
    onMouseLeave,
    activeStep,
}: StepProps) {
    const isActive = activeStep === step;

    return (
        <Wrapper step={step} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <HeadingWrapper isActive={isActive}>
                <StyledHeading accessibilityLevel={3} appearanceLevel={4}>
                    {title}
                </StyledHeading>
                <StepNumber
                    accessibilityLevel={3}
                    color="grey100"
                    fontFamily="text"
                    fontWeight="bold"
                >
                    {step}
                </StepNumber>
            </HeadingWrapper>
            <ImageWrapper step={step} isActive={isActive}>
                <Image priority unoptimized {...image} />
            </ImageWrapper>
            <Paragraph>
                <FormattedReactMessage
                    message={body}
                    replacements={{
                        Link: <Link href="/how-it-works" />,
                    }}
                />
            </Paragraph>
        </Wrapper>
    );
}
